<script setup>
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { tenant, semSegmentation } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    semSegmentation: {
        type: Object,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    exportData,
    selectionIds,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'cmsCustomerId',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ID CMS',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsCustomerId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'email',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Email',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.email,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'eCommerceCustomerBillingAddresses.telephone',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Tél',
                }),
            cell: (props) =>
                props.row.original.defaultECommerceCustomerBillingAddress &&
                props.row.original.defaultECommerceCustomerBillingAddress
                    .telephone
                    ? h(TextCell, {
                          text: props.row.original
                              .defaultECommerceCustomerBillingAddress.telephone,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'lastname',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.lastname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'firstname',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Prénom',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.firstname,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'orderCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Cdes',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.orderCount,
                    tooltipContent:
                        props.row.original.orderNumbers.join('<br>'),
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'turnover',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.turnover,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'averageBasket',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Panier moyen',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.averageBasket,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'refundCount',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Remboursements',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.refundCount,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'refundTurnover',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA<br/>remboursé',
                }),
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.refundTurnover,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'lastOrderDate',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Date<br/>dernière cde',
                }),
            cell: (props) =>
                props.row.original.lastOrderDate
                    ? h(TextCell, {
                          text: formatDate(props.row.original.lastOrderDate),
                      })
                    : null,
        }),
    ],
    url: route('front.projectManagement.sem.postSegmentationCustomers', {
        tenant: tenant.id,
        semSegmentation: semSegmentation.id,
    }),
    exportUrl: route(
        'front.projectManagement.sem.postExportECommerceCustomers',
        {
            tenant: tenant.id,
            semSegmentation: semSegmentation.id,
        },
    ),
    selectionIdKey: 'cmsCustomerId',
    exportSelectionIdKey: 'eCommerceCustomers.cmsCustomerId',
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />
        </template>
    </DataTableLayout>
</template>
